import { Link } from '@tanstack/react-router'

interface ProcessStep {
  step: string
  title: string
  description: string
}

const processSteps: ProcessStep[] = [
  {
    step: '01',
    title: 'Discover & Research',
    description:
      'We begin by deeply understanding your business ecosystem, technical constraints, and strategic goals. Our team conducts thorough audits and market analysis to identify high-impact opportunities.',
  },
  {
    step: '02',
    title: 'Strategy & Planning',
    description:
      'Based on our findings, we architect a robust technical roadmap. We define the tech stack, security protocols, and development milestones to ensure a seamless execution path aligned with your objectives.',
  },
  {
    step: '03',
    title: 'Execute & Develop',
    description:
      'We bring the strategy to life through agile development. Our engineering teams build scalable, secure solutions with regular feedback loops, ensuring every line of code meets industry standards and your exact requirements.',
  },
  {
    step: '04',
    title: 'Optimize & Improve',
    description:
      'Deployment is just the beginning. We continuously monitor system performance, user behavior, and security metrics to refine and improve the solution, ensuring long-term success and adaptability.',
  },
]

interface ProcessSectionProps {
  className?: string
}

export default function ProcessSection({ className }: ProcessSectionProps) {
  return (
    <section
      className={`relative z-10 py-24 container mx-auto px-6 md:px-16 ${className}`}
    >
      <div className="grid lg:grid-cols-12 gap-12 md:gap-16 lg:gap-24">
        <div className="lg:col-span-4 space-y-8 min-w-0">
          <h2 className="text-5xl md:text-7xl font-bold leading-tight">
            Our <br /> Process
          </h2>
          <p className="text-slate-400 text-lg leading-relaxed">
            We follow a rigorous, data-driven approach to solving complex IT
            challenges. From initial discovery to continuous optimization, our
            methodology ensures your business achieves sustainable digital
            transformation.
          </p>
          <div className="pt-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 text-indigo-400 hover:text-indigo-300 font-medium transition-colors"
            >
              <span className="text-xl">↳</span> Get in touch
            </Link>
          </div>
        </div>
        <div className="lg:col-span-8 space-y-12 min-w-0">
          {processSteps.map((item, index) => (
            <div
              key={index}
              className="group border-t border-white/10 pt-12 flex flex-col md:flex-row gap-6 md:gap-12 hover:bg-white/2 transition-colors rounded-xl p-6 mt-6 md:mt-0"
            >
              <div className="text-sm font-mono text-slate-500 pt-2">
                {item.step}
              </div>
              <div className="space-y-4 max-w-2xl min-w-0">
                <div className="flex justify-between items-start">
                  <h3 className="text-2xl md:text-3xl font-bold text-white group-hover:text-indigo-400 transition-colors">
                    {item.title}
                  </h3>
                  <span className="text-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity text-2xl">
                    ¬
                  </span>
                </div>
                <p className="text-slate-400 leading-relaxed">
                  {item.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
